<script setup lang="ts">
import { onBeforeUnmount, onMounted, shallowRef } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { Errors } from '@/@vxapp/@Enums/Error'
import { Endpoint, type EndpointConfig } from '@/@vxapp/@Enums/Endpoint'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { useApi } from '@/@vxapp/@Utils/apiClient'
import { resolveAuthReturnRoute } from '@/plugins/2.router/authReturnRoute'

definePage({ meta: { ...PageMeta.Auth.lobby.meta, pageTitle: PageMeta.Auth.lobby.title } })

const router = useRouter()
const authStore = useAuthStore()
const log = createLogger('AuthLobby')
const returnRoute = shallowRef<unknown>(null)
const resolvedDestination = shallowRef<RouteLocationRaw | null>(null)
const hasFatalError = shallowRef(false)
const errorMessage = shallowRef<string | null>(null)

// Guest attempt owned by this lobby instance until it navigates; null when none is unfinished.
let guestAttempt: number | null = null

const fallbackRoute = { name: PageMeta.Dashboard.ecommerce.name } as RouteLocationRaw

const serviceDestinations = new Map<
  string,
  { endpointName: string; endpoint: EndpointConfig; subject: string }
>([
  [
    PageMeta.Orders.service.name as string,
    {
      endpointName: 'Orders',
      endpoint: Endpoint.Orders.Fetch,
      subject: PageMeta.Orders.service.meta?.subject as string,
    },
  ],
  [
    PageMeta.Invoices.service.name as string,
    {
      endpointName: 'Invoices',
      endpoint: Endpoint.Invoices.Fetch,
      subject: PageMeta.Invoices.service.meta?.subject as string,
    },
  ],
  [
    PageMeta.Quotes.service.name as string,
    {
      endpointName: 'Quotes',
      endpoint: Endpoint.Quotes.Fetch,
      subject: PageMeta.Quotes.service.meta?.subject as string,
    },
  ],
])

type ServiceDestination = {
  endpoint: EndpointConfig
  guid: string
  location: RouteLocationRaw
}

type MatchedPermission = {
  endpoint?: unknown
  subject?: unknown
}

function fail(error: unknown) {
  hasFatalError.value = true
  errorMessage.value = String(error)
  log.error('Invalid authentication lobby request', error)
}

async function pushRoute(location: RouteLocationRaw): Promise<boolean> {
  try {
    const navigationFailure = await router.push(location)

    return !navigationFailure
  } catch (error) {
    log.error('Unable to leave the authentication lobby', error)

    return false
  }
}

async function navigateToFallback() {
  if (await pushRoute(fallbackRoute)) {
    authStore.resetPageMeta()
  }
}

function getPayloadSubject(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('subject' in value)) {
    return null
  }

  return typeof value.subject === 'string' ? value.subject : null
}

function resolveServiceDestination(): ServiceDestination | null {
  const location = resolveAuthReturnRoute(router, returnRoute.value)

  if (!location) {
    fail(Errors.Auth.NO_META_SUBJECT)

    return null
  }

  const resolvedRoute = router.resolve(location)
  const routeName = typeof resolvedRoute.name === 'string' ? resolvedRoute.name : null
  const serviceDestination = routeName ? serviceDestinations.get(routeName) : null

  if (!serviceDestination) {
    fail(Errors.Auth.INVALID_ENDPOINT)

    return null
  }

  const payloadSubject = getPayloadSubject(returnRoute.value)
  const matchedPermission = authStore.matchedPermission as MatchedPermission | null
  const permissionSubject = matchedPermission?.subject

  if (typeof permissionSubject !== 'string' || !permissionSubject) {
    fail(Errors.Auth.NO_PERMISSION_SUBJECT)

    return null
  }

  if (
    payloadSubject !== permissionSubject ||
    resolvedRoute.meta.subject !== permissionSubject ||
    serviceDestination.subject !== permissionSubject
  ) {
    fail(Errors.Auth.SUBJECT_MISMATCH)

    return null
  }

  if (matchedPermission?.endpoint !== serviceDestination.endpointName) {
    fail(Errors.Auth.INVALID_ENDPOINT)

    return null
  }

  const guid = (resolvedRoute.params as Record<string, unknown>).guid

  if (typeof guid !== 'string' || !guid.trim()) {
    fail(Errors.Auth.NO_GUID_SET)

    return null
  }

  return {
    endpoint: serviceDestination.endpoint,
    guid,
    location,
  }
}

async function navigateToServiceDestination(clearPageMetaAfterNavigation: boolean) {
  if (!resolvedDestination.value || !(await pushRoute(resolvedDestination.value))) {
    return false
  }

  if (clearPageMetaAfterNavigation) {
    authStore.resetPageMeta()
  }

  return true
}

// Superseded means something newer took over; this attempt's own failure is not superseded.
function isGuestAttemptSuperseded(attempt: number): boolean {
  if (!authStore.isGuestAttemptSuperseded(attempt)) {
    return false
  }

  log.debug('Guest authentication attempt superseded -> ignoring its result')

  return true
}

async function onProceed() {
  // Proceed while an attempt is in flight navigates away, and the unmount supersedes the attempt.
  if (resolvedDestination.value) {
    await navigateToServiceDestination(false)

    return
  }

  await navigateToFallback()
}

onMounted(async () => {
  log.debug('Validating authentication lobby request')
  returnRoute.value = authStore.pageMeta

  const serviceDestination = resolveServiceDestination()

  if (!serviceDestination) {
    await navigateToFallback()

    return
  }

  resolvedDestination.value = serviceDestination.location

  const authStatus = authStore.getAuthStatus()

  if (authStatus.isAuthenticated) {
    log.debug('Authenticated session detected -> restoring the service route')
    await navigateToServiceDestination(true)

    return
  }

  const attempt = authStore.beginGuestAuthentication()

  guestAttempt = attempt

  try {
    const { headers } = await useApi.apiCall(serviceDestination.endpoint, {
      guid: serviceDestination.guid,
    })

    if (isGuestAttemptSuperseded(attempt)) {
      return
    }

    const success = await authStore.processGuestAuthentication(headers, attempt)

    if (isGuestAttemptSuperseded(attempt)) {
      return
    }

    if (!success) {
      guestAttempt = null
      fail(Errors.Auth.GUEST_AUTH_FAILED)

      return
    }

    await authStore.fetchPermissions(true, attempt)

    if (isGuestAttemptSuperseded(attempt)) {
      return
    }

    guestAttempt = null
    await navigateToServiceDestination(false)
  } catch (error) {
    if (isGuestAttemptSuperseded(attempt)) {
      return
    }

    guestAttempt = null
    log.error('Guest authentication request failed', error)
    fail(Errors.Auth.GUEST_AUTH_FAILED)
  }
})

onBeforeUnmount(() => {
  if (guestAttempt === null) {
    return
  }

  authStore.supersedeGuestAuthentication(guestAttempt)
  guestAttempt = null
})
</script>

<template>
  <div class="reauth-wrapper">
    <div class="reauth-content text-center">
      <VProgressCircular
        v-if="!hasFatalError"
        indeterminate
        size="64"
        width="4"
        color="primary"
        class="mb-6"
      />
      <VIcon
        v-else
        icon="tabler-activity-heartbeat"
        :size="64"
        color="error"
        class="mb-6"
      />

      <VCardTitle
        v-if="!hasFatalError"
        class="reauth-title mb-2"
      >
        Authenticating your request
      </VCardTitle>
      <VCardTitle v-else>Authentication Error</VCardTitle>
      <VCardText
        v-if="!hasFatalError"
        class="reauth-subtitle mb-8"
        >Please wait</VCardText
      >
      <VCardText v-else>
        I've stumbled upon to some mischief

        <VCardSubtitle>
          {{ errorMessage }}
        </VCardSubtitle>
      </VCardText>

      <div class="reauth-actions">
        <VBtn
          v-if="!hasFatalError"
          block
          color="primary"
          class="mb-4"
          @click="onProceed"
          >Proceed</VBtn
        >
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.reauth-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
}

.reauth-content {
  max-width: 360px;
  width: 100%;
}

.reauth-title {
  font-size: 1.75rem;
  font-weight: 500;
}

.reauth-actions {
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;

  .v-btn {
    font-weight: 500;
  }
}

.reauth-illustration {
  margin-bottom: 2rem;

  img {
    max-width: 100%;
  }
}

.reauth-footer-mask {
  position: absolute;
  bottom: 0;
  width: 100%;
}
</style>
